import {Component, HostListener, OnInit} from "@angular/core";
import {SelectionService} from "../../../services/selection.service";
import {MalfunctionStatus, Report} from "../../../shared/interfaces/report";
import {BootstrapIcon, HeaderService} from "../../../services/header.service";
import {CookieService} from "ngx-cookie-service";
import {ReportService} from "../data-access/report.service";
import {UserService} from "src/app/services/user.service";
import {KnowledgeBaseService} from "src/app/features/knowledge-database/data-access/knowledge-base.service";
import {Userrole} from "src/app/shared/interfaces/user";
import {NotificationService} from "../../../shared/data-access/notification.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {Observable, tap} from "rxjs";
import {TreeNode} from "../../knowledge-database/kd-workplace/kd-workplace.component";
import {LocalStorageService} from "../../../shared/data-access/local-storage.service";
import {IncidentIdService} from "../data-access/incident-id.service";
import {Router} from "@angular/router";
import {ComponentCanDeactivate} from "../../../core/guards/leave-active-report.guard";
import {NavigationService} from "../../../shared/data-access/navigation.service";


@Component({
  selector: "app-er-dashboard",
  templateUrl: "./er-dashboard.component.html",
  styleUrls: ["./er-dashboard.component.scss"],
})
export class ErDashboardComponent implements OnInit, ComponentCanDeactivate {

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent) {
    if (!this.navigationService.skipDeactivateGuard()) {
      event.returnValue = true;
      return true;
    }
    return false;
  }

  canDeactivate() {
    if (window.confirm('Wollen Sie die aktive Stillstandsmeldung wirklich verwerfen?')) {
      this.reportService.cancelReport$.next();
      return true
    }
    return false;
  }

  constructor(
    private readonly selectionService: SelectionService,
    protected readonly knowledgeBaseService: KnowledgeBaseService,
    private readonly headerService: HeaderService,
    protected readonly reportService: ReportService,
    protected readonly userService: UserService,
    protected readonly incidentIdService: IncidentIdService,
    private cookieService: CookieService,
    private localStorageService: LocalStorageService,
    private readonly notificationService: NotificationService,
    private navigationService: NavigationService,
    private readonly router: Router
  ) {
    this.reportService.currentReport$.next(null);
    this.headerService.setBackgroundColor("#d03508");
    this.headerService.setHeaderTitle("Stillstandsmeldung");
    this.headerService.setHeaderSubTitle("Übersicht");
    this.headerService.setHeaderIcon(BootstrapIcon.standstill);


    this.selectionService.currentWorkplace.pipe(takeUntilDestroyed()).subscribe((workplaceId) => {
      if (workplaceId) {
        this.reportService.update$.next({
          properties: {
            workplace: workplaceId
          }
        })
      }
    });
    this.selectionService.currentCategory.pipe(takeUntilDestroyed()).subscribe((categoryId) => {
      if (categoryId) {
        this.reportService.update$.next({
          properties: {
            category: categoryId
          }
        })
      }
    });
    this.selectionService.currentMalfunction.pipe(takeUntilDestroyed()).subscribe((malfunctionId) => {
      if (malfunctionId) {
        this.reportService.update$.next({
          properties: {
            malfunction: malfunctionId,
            status: MalfunctionStatus.CREATED
          }
        })
      }
    });
    this.incidentIdService.incidentId$.pipe(takeUntilDestroyed()).subscribe((incidentId) => {
      if (incidentId) {
        this.reportService.update$.next({
          properties: {
            incident_id: incidentId
          }
        })
      }
    })
  }

  Userrole = Userrole;

  ngOnInit() {
    this.reportService.reportClosed.set(false);
    this.navigationService.preventNavigation();
    this.knowledgeBaseService.initialize()
    this.initializeNewReport().subscribe((() => {
      /*
   * if userrole is Mitarbeiter, incidentId is already fixed when the report is created. Therefore, it won't be
   *  updated later and saved to the report object. In this case we need to add the correct incidentId to the
   *  initial report
   */
      if (this.userService.userrole() === Userrole.Mitarbeiter) {
        this.reportService.update$.next({
          properties: {
            incident_id: this.incidentIdService.incidentId()
          }
        })
      }
    }))
  }

  private initializeNewReport() {
    this.initializeUserSpecificSettings()
    return this.createNewReport().pipe(
      tap((report: Report) => {
        this.notificationService.showNotification('Neue Stillstandsmeldung erzeugt');
        this.localStorageService.saveToLocalStorage({key: 'currentReport', value: JSON.stringify(report)})
        this.reportService.currentReport$.next(report)
      }),
    )
  }

  private initializeUserSpecificSettings() {
    this.knowledgeBaseService.resetSelectedItems()
    const userrole = this.userService.userrole() as Userrole

    if (userrole === Userrole.Mitarbeiter) {
      this.initializeWorkerSettings()
    } else if ([Userrole.Teamleiter, Userrole.Schichtleiter].includes(userrole)) {
      this.initializeLeaderSettings()
    }
  }

  private initializeWorkerSettings() {
    const [productionLineId, productionLineName] = this.cookieService
      .get("productionLine")
      .split("%");
    const [workplaceId, workplaceName] = this.cookieService
      .get("workplace")
      .split("%");

    this.updateProductionLine({
      id: productionLineId,
      name: productionLineName,
      type: "productionLine"
    });

    this.updateWorkplace({
      id: workplaceId,
      name: workplaceName,
      type: "workplace"
    });

    this.knowledgeBaseService.changeActivePage(1);
  }

  private initializeLeaderSettings() {
    this.updateProductionLine({
      id: this.userService.userProductionLineId(),
      name: this.userService.userProductionLineName(),
      type: "productionLine"
    });

    this.knowledgeBaseService.changeActivePage(0);
  }

  private updateProductionLine(item: TreeNode) {
    this.knowledgeBaseService.selectProductionLine(item.id, true);
    this.knowledgeBaseService.updateSelectedItems("productionLine", item);
  }

  private updateWorkplace(item: TreeNode) {
    this.knowledgeBaseService.selectWorkplace(item.id);
    this.knowledgeBaseService.updateSelectedItems("workplace", item);
    this.selectionService.changeWorkplace(item.id);
  }

  createNewReport(): Observable<Report> {
    return this.reportService.addNewReport({
      created_at: new Date().toISOString(),
      created_by: this.userService.user()!.id,
      status: MalfunctionStatus.CREATED,
      workplace: this.userService.userrole() === Userrole.Mitarbeiter
        ? this.cookieService
          .get("workplace")
          .split("%")[0]
        : null,
    })
  }


}
